import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { NavLink, useNavigate, useSearchParams } from "react-router-dom";
import { Input, Select, Popconfirm, Spin, message, notification } from "antd";
import { RightOutlined, SearchOutlined } from "@ant-design/icons";
import {
  AlertMessage,
  EpisodeDrawer,
  type EpisodeDrawerHandle,
  HomeSeriesPagination,
} from "../../components/common";
import {
  deleteSeriesLanguageApi,
  getSeriesDetailApi,
  getSeriesLanguagesPageApi,
} from "../../service/series";
import { getTaskListApi } from "../../service/folders";
import { normalizeCompletionPercent } from "../../utils/completionPercent";
import { getLanguageName, getLanguageNativeDisplay } from "../../utils/languageUtils";
import { getVideoPlayUrl } from "../../utils/videoUrl";
import {
  downloadManyVideosMemorySafe,
  sanitizeVideoFileBaseName,
} from "../../utils/batchVideoDownload";
import {
  createDownloadProgressNotificationKey,
  updateDownloadProgressNotification,
} from "../../utils/downloadProgressNotification";
import { useSeriesButtonPermissions } from "../../hooks/useSeriesButtonPermissions";
import { useUserRole } from "../../hooks/useUserRole";
import { useAlert } from "../../hooks/useAlert";
import { ALERT_Z_INDEX } from "../../utils/alertManager";
import {
  INNER_SERIES_LIST_PAGE_PARAM,
  OUTER_SERIES_LIST_PAGE_PARAM,
  PREMIUM_VIDEO_LIST_REFACTOR_BASE,
} from "../../utils/menuPathNormalize";
import {
  LucideDownloadIcon,
  LucidePlaneIcon,
  LucideTrash2Icon,
} from "./premiumSeriesLucideIcons";
import { PageBackLink } from "../home/PageBack";
import { useSeriesBackTarget } from "../home/useSeriesBackTarget";
import { useSeriesContextMeta } from "../home/useSeriesContextMeta";
import "./premiumSeriesInnerPage.scss";

/** 精品内层语种卡片主标题：优先接口 langName / targetLangName，否则回退 getLanguageName */
function seriesLanguageZhDisplay(item: Series.SeriesLanguageItem): string {
  if (item.langName?.trim()) return item.langName.trim();
  const t = item.targetLangName ?? item.target_lang_name;
  if (t != null && String(t).trim() !== "") return String(t).trim();
  return getLanguageName(item.langCode);
}

/**
 * 多语种 `tagStatus`（与 `api.d.ts`、剧集管理列表数据源一致）：
 * 0 未提交（不展示状态标签）、1 审核通过、2 需返工、3 已提交（审核员视角文案为「待审核」）、4 已修改。
 * 剧集管理页 `JujiManage` 表格里原先用同一套中文；仅 tag=3 在审核员与其它角色之间区分措辞。
 */
type SeriesLangStatusBadge =
  | "not-submitted"
  | "approved"
  | "need-rework"
  | "submitted"
  | "pending-review"
  | "modified"
  | "empty";

const STATUS_LABEL: Record<SeriesLangStatusBadge, string> = {
  "not-submitted": "未提交",
  approved: "审核通过",
  "need-rework": "需返工",
  submitted: "已提交",
  "pending-review": "待审核",
  modified: "已修改",
  empty: "—",
};

function seriesLangStatusBadge(
  tag: number | null | undefined,
  isAssessor: boolean
): SeriesLangStatusBadge {
  if (tag === null || tag === undefined) return "empty";
  switch (tag) {
    case 0:
      return "not-submitted";
    case 1:
      return "approved";
    case 2:
      return "need-rework";
    case 3:
      return isAssessor ? "pending-review" : "submitted";
    case 4:
      return "modified";
    default:
      return "empty";
  }
}

type SortOption = "latest" | "oldest" | "name";

function resolveApiSort(sortBy: SortOption): number {
  if (sortBy === "name") return 3;
  return sortBy === "oldest" ? 2 : 1;
}

function parseInnerListPageQuery(pageVal: string | null): number {
  const n = parseInt(pageVal || "1", 10);
  return Math.max(1, Number.isFinite(n) ? n : 1);
}

/**
 * 精品译制 · 内层剧集页：多语种列表走 getSeriesLanguagesPageApi，译制进度仅使用接口字段 completionPercent。
 */
export const PremiumSeriesInnerPage: FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { checkSeriesButtonPermission, hasSeriesDeletePermission } = useSeriesButtonPermissions();
  const { isAssessor } = useUserRole();
  const { alertConfig, hideAlert, showError, showSuccess } = useAlert();
  const [api, contextHolder] = notification.useNotification();

  /** 页面级返回与剧集信息 meta pill（原 Header leading/meta，Header 移除后由页面渲染） */
  const backTarget = useSeriesBackTarget();
  const seriesMetaPill = useSeriesContextMeta(false);

  const seriesIdStr = searchParams.get("seriesId") ?? "";
  const seriesNameParam = searchParams.get("seriesName") ?? "";
  const highlightLangParam = (searchParams.get("highlightLang") ?? "").trim();
  const seriesIdNum = useMemo(() => {
    const n = Number(seriesIdStr);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [seriesIdStr]);

  const decodedNameFromUrl = useMemo(() => {
    if (!seriesNameParam) return "";
    try {
      return decodeURIComponent(seriesNameParam);
    } catch {
      return seriesNameParam;
    }
  }, [seriesNameParam]);

  const episodeDrawerRef = useRef<EpisodeDrawerHandle | null>(null);
  const highlightScrollKeyDoneRef = useRef<string>("");
  const [searchInput, setSearchInput] = useState("");
  const [debouncedLangName, setDebouncedLangName] = useState("");
  const [sortBy, setSortBy] = useState<SortOption>("latest");
  const [page, setPage] = useState(() => parseInnerListPageQuery(searchParams.get("page")));
  const pageSize = 9;

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<Series.SeriesLanguageItem[]>([]);
  const [total, setTotal] = useState(0);

  const [detailName, setDetailName] = useState("");
  const [characterProfiles, setCharacterProfiles] = useState<Series.CharacterProfile[]>([]);
  const [sourceLangCode, setSourceLangCode] = useState("zh");
  const [sourceLangName, setSourceLangName] = useState("");
  const [needRemovalSubtitle, setNeedRemovalSubtitle] = useState(false);

  const hasSeriesDownloadPermission = useCallback(
    () => checkSeriesButtonPermission("series:download"),
    [checkSeriesButtonPermission]
  );

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedLangName(searchInput.trim()), 400);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  const innerListPageQs = searchParams.get("page");
  useEffect(() => {
    const n = parseInnerListPageQuery(innerListPageQs);
    setPage((prev) => (prev === n ? prev : n));
  }, [innerListPageQs]);

  const pageInUrl = searchParams.get("page");
  useEffect(() => {
    if (page <= 1) {
      if (!pageInUrl) return;
    } else if (pageInUrl === String(page)) {
      return;
    }
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (page <= 1) next.delete("page");
        else next.set("page", String(page));
        return next;
      },
      { replace: true }
    );
  }, [page, pageInUrl, setSearchParams]);

  const prevSearchSortKeyRef = useRef<string | null>(null);
  useEffect(() => {
    const key = `${debouncedLangName}\0${sortBy}`;
    if (prevSearchSortKeyRef.current === null) {
      prevSearchSortKeyRef.current = key;
      return;
    }
    if (prevSearchSortKeyRef.current !== key) {
      prevSearchSortKeyRef.current = key;
      setPage(1);
    }
  }, [debouncedLangName, sortBy]);

  const prevSeriesIdForPagerRef = useRef<number | null>(null);
  useEffect(() => {
    if (prevSeriesIdForPagerRef.current !== null && prevSeriesIdForPagerRef.current !== seriesIdNum) {
      setPage(1);
    }
    prevSeriesIdForPagerRef.current = seriesIdNum;
  }, [seriesIdNum]);

  useEffect(() => {
    if (!seriesIdNum) return;

    const loadDetail = async () => {
      try {
        const res = await getSeriesDetailApi(seriesIdNum);
        if (res?.code === 0 && res.data) {
          setDetailName(res.data.name || "");
          setSourceLangCode(res.data.sourceLangCode || "zh");
          setSourceLangName(res.data.sourceLangName || "");
          setNeedRemovalSubtitle(!!res.data.needRemovalSubtitle);
          setCharacterProfiles(res.data.characterProfiles ?? []);
        }
      } catch (e) {
        console.error(e);
      }
    };
    void loadDetail();
  }, [seriesIdNum]);

  const fetchList = useCallback(async () => {
    if (!seriesIdNum) {
      setRows([]);
      setTotal(0);
      return;
    }
    setLoading(true);
    try {
      const sort = resolveApiSort(sortBy);
      const params: Series.GetSeriesLanguagesPageParams = {
        pageNo: page,
        pageSize,
        seriesId: seriesIdNum,
        sort,
      };
      if (debouncedLangName) {
        params.langName = debouncedLangName;
      }
      const res = await getSeriesLanguagesPageApi(params);
      if (res.code !== 0) {
        showError(res.msg || "获取语种列表失败");
        setRows([]);
        setTotal(0);
        return;
      }
      const list = res.data?.list ?? [];
      const tot = res.data?.total ?? 0;
      setTotal(tot);
      setRows(list);
    } catch (e) {
      console.error(e);
      showError("获取语种列表失败，请稍后重试");
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [
    seriesIdNum,
    page,
    pageSize,
    sortBy,
    debouncedLangName,
    showError,
  ]);

  useEffect(() => {
    void fetchList();
  }, [fetchList]);

  useEffect(() => {
    highlightScrollKeyDoneRef.current = "";
  }, [seriesIdStr]);

  useEffect(() => {
    if (!highlightLangParam || loading) return;
    const key = `${seriesIdStr}|${highlightLangParam}`;
    if (highlightScrollKeyDoneRef.current === key) return;

    const stripHighlight = () => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete("highlightLang");
          return next;
        },
        { replace: true }
      );
    };

    const normalized = highlightLangParam.toLowerCase();
    const hit = rows.find((r) => String(r.langCode || "").toLowerCase() === normalized);
    if (!hit) {
      highlightScrollKeyDoneRef.current = key;
      stripHighlight();
      return;
    }

    const tryScroll = () => {
      const el = document.querySelector(`[data-series-inner-lang="${hit.langCode}"]`);
      if (el instanceof HTMLElement) {
        el.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }
    };

    highlightScrollKeyDoneRef.current = key;
    tryScroll();
    window.requestAnimationFrame(tryScroll);
    stripHighlight();
  }, [highlightLangParam, loading, rows, seriesIdStr, setSearchParams]);

  const displaySeriesName = detailName || decodedNameFromUrl || "剧集名称";
  const displayTitle = `${displaySeriesName} — 原剧管理`;

  const drawerEpisodeInfo = useMemo(
    () => ({
      name: displaySeriesName,
      sourceLanguage: sourceLangCode,
      sourceLangName,
      targetLanguage: sourceLangCode,
      needRemovalSubtitle,
    }),
    [displaySeriesName, sourceLangCode, sourceLangName, needRemovalSubtitle]
  );

  const buildVideoListTo = (langCode: string, langName?: string) => {
    if (!seriesIdNum) return "#";
    const q = new URLSearchParams({
      seriesId: String(seriesIdNum),
      seriesName: displaySeriesName,
      langCode,
    });
    if (langName?.trim()) {
      q.set("langName", langName.trim());
    }
    if (page > 1) {
      q.set(INNER_SERIES_LIST_PAGE_PARAM, String(page));
    }
    const outerRaw = searchParams.get(OUTER_SERIES_LIST_PAGE_PARAM);
    if (outerRaw) {
      const on = parseInt(outerRaw, 10);
      if (Number.isFinite(on) && on > 1) {
        q.set(OUTER_SERIES_LIST_PAGE_PARAM, String(on));
      }
    }
    return `${PREMIUM_VIDEO_LIST_REFACTOR_BASE}?${q.toString()}`;
  };

  const pages = Math.max(1, Math.ceil(total / pageSize));
  const hasPrev = page > 1;
  const hasNext = page < pages;

  const handleHeroClick = () => {
    if (!seriesIdNum) {
      message.warning("缺少剧集 ID");
      return;
    }
    const q = new URLSearchParams({
      seriesId: String(seriesIdNum),
      seriesName: displaySeriesName,
    });
    if (page > 1) {
      q.set(INNER_SERIES_LIST_PAGE_PARAM, String(page));
    }
    const outerRaw = searchParams.get(OUTER_SERIES_LIST_PAGE_PARAM);
    if (outerRaw) {
      const on = parseInt(outerRaw, 10);
      if (Number.isFinite(on) && on > 1) {
        q.set(OUTER_SERIES_LIST_PAGE_PARAM, String(on));
      }
    }
    navigate(`${PREMIUM_VIDEO_LIST_REFACTOR_BASE}?${q.toString()}`);
  };

  const handleLanguageDownload = async (item: Series.SeriesLanguageItem) => {
    if (!seriesIdNum) {
      message.error("缺少剧集信息");
      return;
    }
    if (!hasSeriesDownloadPermission()) {
      message.error("暂无剧集下载权限");
      return;
    }

    const notificationKey = createDownloadProgressNotificationKey();
    const updateProgress = (progress: number, text: string) => {
      updateDownloadProgressNotification(api, notificationKey, progress, text);
    };

    try {
      updateProgress(0, "正在获取视频列表...");
      const videoListResponse = await getTaskListApi({
        seriesId: seriesIdNum,
        langCode: item.langCode,
      });
      const completedVideos = (videoListResponse.data || []).filter(
        (video: { status: number }) => video.status === 2 && getVideoPlayUrl(video as never)
      );

      if (completedVideos.length === 0) {
        api.destroy(notificationKey);
        message.error("该语种下没有可下载的已完成视频");
        return;
      }

      updateProgress(5, `找到 ${completedVideos.length} 个可下载视频，准备开始下载...`);
      await new Promise((resolve) => setTimeout(resolve, 300));

      const items = completedVideos
        .map((video: { name?: string; status?: number }) => {
          const videoUrl = getVideoPlayUrl(video as never);
          if (!videoUrl) return null;
          return { url: videoUrl, baseName: video.name || "video" };
        })
        .filter((x): x is { url: string; baseName: string } => x !== null);

      const safeSeriesName = sanitizeVideoFileBaseName(displaySeriesName || "series");
      const languageName = seriesLanguageZhDisplay(item);
      const safeLanguageName = sanitizeVideoFileBaseName(languageName);
      const folderName = `${safeSeriesName}_${safeLanguageName}`;
      const zipDownloadFileName = `${folderName}.zip`;

      const result = await downloadManyVideosMemorySafe({
        items,
        zipDownloadFileName,
        folderName,
        onProgress: (p) => {
          const unitProgress =
            p.fileProgress != null ? p.current + p.fileProgress : p.current;
          const pct =
            p.phase === "done"
              ? 100
              : 5 + Math.round((unitProgress / Math.max(p.total, 1)) * 90);
          updateProgress(pct, p.message);
        },
      });

      api.destroy(notificationKey);

      if (result.aborted) return;
      if (result.unsupportedDirectoryPicker) {
        message.error("当前浏览器不支持选择文件夹保存，请使用 Chrome 或 Edge");
        return;
      }
      if (result.success === 0) {
        message.error("所有视频保存失败，请检查网络连接");
        return;
      }
      if (result.fail > 0) {
        message.warning(`部分失败：成功 ${result.success} 集，失败 ${result.fail} 集`);
      } else {
        message.success(`已成功保存 ${result.success} 集视频到所选文件夹`);
      }
    } catch (err) {
      console.error(err);
      api.destroy(notificationKey);
      message.error("下载失败，请稍后重试");
    }
  };

  const handleDeleteLanguage = async (item: Series.SeriesLanguageItem) => {
    try {
      const res = await deleteSeriesLanguageApi(item.id);
      if (res.code === 0) {
        showSuccess("已删除该语种");
        await fetchList();
      } else {
        showError(res.msg || "删除失败");
      }
    } catch {
      showError("删除失败，请稍后重试");
    }
  };

  if (!seriesIdNum) {
    return (
      <div className="premium-series-inner">
        <div className="premium-series-inner__empty">缺少有效的剧集 ID，请从剧集列表进入。</div>
      </div>
    );
  }

  return (
    <div className="premium-series-inner">
      {contextHolder}
      <AlertMessage config={alertConfig} onClose={hideAlert} zIndex={ALERT_Z_INDEX.MY_WORK} />

      <button
        type="button"
        className="premium-series-inner__hero"
        onClick={handleHeroClick}
        aria-label={`进入视频列表：${displayTitle}`}
      >
        <div className="premium-series-inner__hero-decor1" aria-hidden />
        <div className="premium-series-inner__hero-decor2" aria-hidden />
        <div className="premium-series-inner__hero-inner">
          <div className="premium-series-inner__hero-left">
            <div className="premium-series-inner__hero-icon" aria-hidden>
              <LucidePlaneIcon size={32} className="premium-series-inner__lucide" />
            </div>
            <div>
              <h2 className="premium-series-inner__hero-title">{displayTitle}</h2>
              <p className="premium-series-inner__hero-tip">
                Tips：点击本区域或右侧按钮，前往该剧集视频列表
              </p>
            </div>
          </div>
          <span className="premium-series-inner__hero-upload">
            <span>点击上传视频</span>
            <RightOutlined style={{ fontSize: 14 }} aria-hidden />
          </span>
        </div>
      </button>

      <div className="premium-series-inner__section-head">
        <div className="premium-series-inner__section-title-wrap">
          {backTarget ? (
            <PageBackLink to={backTarget} aria-label="返回剧集列表" />
          ) : null}
          <h3 className="premium-series-inner__section-title">多语种版本</h3>
          <span className="home-series__meta home-series__meta--pill">{seriesMetaPill}</span>
        </div>
        <div className="premium-series-inner__filters">
          <div className="premium-series-inner__search">
            <Input
              allowClear
              size="middle"
              placeholder="搜索语种..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              prefix={<SearchOutlined style={{ color: "#94a3b8" }} />}
            />
          </div>
          <Select
            className="premium-series-inner__select"
            size="middle"
            value={sortBy}
            onChange={(v) => setSortBy(v)}
            options={[
              { value: "latest", label: "最新创建" },
              { value: "oldest", label: "最早创建" },
              { value: "name", label: "按名称排序" },
            ]}
            getPopupContainer={(n) => n.parentElement ?? document.body}
          />
          <div className="premium-series-inner__view-toggle">
            <button
              type="button"
              className="premium-series-inner__view-btn premium-series-inner__view-btn--single"
              onClick={() => episodeDrawerRef.current?.toggleDrawer()}
              aria-label="打开剧集信息"
            >
              <i
                className="iconfont icon-shouqicebianlan premium-series-inner__view-drawer-icon"
                aria-hidden
              />
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="premium-series-inner__loading">
          <Spin size="large" />
        </div>
      ) : rows.length === 0 ? (
        <div className="premium-series-inner__empty">暂无多语种版本</div>
      ) : (
        <div className="premium-series-inner__grid">
          {rows.map((card) => {
            const zhName = seriesLanguageZhDisplay(card);
            const native = getLanguageNativeDisplay(card.langCode);
            const initials = zhName.charAt(0) || card.langCode.charAt(0).toUpperCase();
            const langStatusBadge = seriesLangStatusBadge(card.tagStatus, isAssessor);
            const displayPct = normalizeCompletionPercent(card.completionPercent) ?? 0;
            return (
              <div
                key={card.id}
                className="premium-series-inner__card"
                data-series-inner-lang={card.langCode}
              >
                <div className="premium-series-inner__card-glow" />
                <NavLink
                  to={buildVideoListTo(card.langCode, card.langName ?? undefined)}
                  className="premium-series-inner__card-main"
                >
                  <div className="premium-series-inner__card-head">
                    <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
                      <div className="premium-series-inner__lang-badge">{initials}</div>
                      <div>
                        <p className="premium-series-inner__lang-name">{zhName}</p>
                        <p className="premium-series-inner__lang-native">{native}</p>
                      </div>
                    </div>
                  </div>
                  <div className="premium-series-inner__card-body">
                    <div className="premium-series-inner__progress-label">
                      <span>译制进度</span>
                      <span style={{ fontWeight: 500 }}>{displayPct}%</span>
                    </div>
                    <div className="premium-series-inner__progress-track">
                      <div
                        className={`premium-series-inner__progress-bar${
                          displayPct === 0 ? " premium-series-inner__progress-bar--empty" : ""
                        }`}
                        style={{ width: `${displayPct}%` }}
                      />
                    </div>
                  </div>
                </NavLink>
                <div className="premium-series-inner__card-footer">
                  {card.tagStatus !== 0 ? (
                    <span
                      className={`premium-series-inner__status premium-series-inner__status--${langStatusBadge}`}
                    >
                      {STATUS_LABEL[langStatusBadge]}
                    </span>
                  ) : null}
                  <div className="premium-series-inner__card-actions">
                    {hasSeriesDownloadPermission() ? (
                      <button
                        type="button"
                        className="premium-series-inner__icon-btn"
                        title="下载"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          void handleLanguageDownload(card);
                        }}
                      >
                        <LucideDownloadIcon size={16} className="premium-series-inner__lucide" />
                      </button>
                    ) : null}
                    {hasSeriesDeletePermission() ? (
                      <Popconfirm
                        title="确定要删除这个剧集语种吗？"
                        okText="确定"
                        cancelText="取消"
                        onPopupClick={(e) => {
                          e.stopPropagation();
                        }}
                        onConfirm={() => {
                          void handleDeleteLanguage(card);
                        }}
                      >
                        <button
                          type="button"
                          className="premium-series-inner__icon-btn"
                          title="删除"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                        >
                          <LucideTrash2Icon size={16} className="premium-series-inner__lucide" />
                        </button>
                      </Popconfirm>
                    ) : null}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {total > 0 && !loading ? (
        <div className="premium-series-inner__pager">
          <HomeSeriesPagination
            page={page}
            pageCount={pages}
            hasPrev={hasPrev}
            hasNext={hasNext}
            onPagePrev={() => setPage((p) => Math.max(1, p - 1))}
            onPageNext={() => setPage((p) => Math.min(pages, p + 1))}
          />
        </div>
      ) : null}

      <EpisodeDrawer
        ref={episodeDrawerRef}
        showToggleButton={false}
        episodeInfo={drawerEpisodeInfo}
        speakersData={[]}
        speakersLoading={false}
        seriesName={drawerEpisodeInfo.name}
        characterProfiles={characterProfiles}
      />
    </div>
  );
};
